/**
 * The builder's model of an automation: a tree of steps where a branch holds
 * its two paths (`yes`, `no`), and the lossless conversion to and from the
 * stored `AutomationGraph`. Paths never join, so a chain is `next`-linked and
 * a branch node's `whenTrue` / `whenFalse` point at the heads of its paths.
 */
import type { ActionType } from "../../shared/config";
import type {
  Attachment,
  AutomationGraph,
  BranchAsk,
  BranchCompare,
  BranchKind,
  BranchStepConfig,
  EmailStepConfig,
  FactActionConfig,
  GraphNode,
  NotifyStepConfig,
  RoomStepConfig,
  StepConfig,
  StopWhen,
  TriggerJson,
  WaitMode,
  WaitStepConfig,
  WebhookStepConfig,
} from "../../shared/types";
import { emailNeeds } from "../../shared/automation-words";
import { normalizeTag } from "../../shared/tags";
import { friendlyTokens, richToText } from "../../shared/tokens";
import { plainToRich } from "../components/RichBox";

export type WaitUnit = WaitStepConfig["unit"];

export interface EmailStep {
  uid: string;
  type: "email";
  subject: string;
  /** The message body as plain words, for a step written before the rich box. */
  preview: string;
  /** The message body as html, from the rich box. Wins over `preview`. */
  previewHtml: string;
  /** Files delivered with the email. */
  attachments: Attachment[];
  /** The button's words; it opens their page. Empty: no button. */
  button: string;
  /** Pictures shown in the email, under the message. */
  images: Attachment[];
  metrics?: Array<[string, string]>;
}
export interface RoomStep {
  uid: string;
  type: "room";
  title: string;
  note: string;
  /** The note as html, from the rich box. Wins over `note`. */
  noteHtml: string;
  attachments: Attachment[];
  notifyEnabled: boolean;
  notifySubject: string;
}
export interface FactStep {
  uid: string;
  type: "fact";
  op: "add" | "remove";
  name: string;
}
export interface WebhookStep {
  uid: string;
  type: "webhook";
  url: string;
}
export interface NotifyStep {
  uid: string;
  type: "notify";
  email: string;
  subject: string;
  message: string;
}
export interface WaitStep {
  uid: string;
  type: "wait";
  mode: WaitMode;
  num: number;
  unit: WaitUnit;
}
export interface BranchStep {
  uid: string;
  type: "branch";
  ask: BranchAsk;
  yes: Step[];
  no: Step[];
}
export type Step = EmailStep | RoomStep | FactStep | WebhookStep | NotifyStep | WaitStep | BranchStep;
export type StepType = Step["type"];

/** A node id: unique enough for a graph, and never reused across reloads. */
export function newId(): string {
  return `n_${Math.random().toString(36).slice(2, 10)}`;
}

// ── new steps, as the + menu makes them ────────────────────────────────
/** A new email starts as the design's does (c-build/flow.js `feNew`): no subject yet, a greeting, and its button. */
const NEW_EMAIL_WORDS = "Hi {{first name}},";

export function newStep(type: StepType): Step {
  const uid = newId();
  switch (type) {
    case "email":
      return { uid, type, subject: "", preview: NEW_EMAIL_WORDS, previewHtml: plainToRich(NEW_EMAIL_WORDS), attachments: [], button: "Open your page", images: [] };
    case "room":
      return { uid, type, title: "An update on their page", note: "", noteHtml: "", attachments: [], notifyEnabled: true, notifySubject: "" };
    case "fact":
      return { uid, type, op: "add", name: "" };
    case "webhook":
      return { uid, type, url: "" };
    case "notify":
      return { uid, type, email: "", subject: "", message: "" };
    case "wait":
      return { uid, type, mode: "after", num: 1, unit: "days" };
    case "branch":
      return { uid, type, ask: { kind: "tag", tag: "watched", cmp: "more", a: 50, b: 75 }, yes: [], no: [] };
  }
}

// ── config ⇄ step ───────────────────────────────────────────────────────
const KINDS: readonly BranchKind[] = ["tag", "notag", "pct", "min", "idle"];
const COMPARES: readonly BranchCompare[] = ["more", "less", "between"];

function num(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

/** A stored ask, cleaned. Every field is present so the editor never reads undefined. */
export function cleanAsk(raw: unknown): BranchAsk {
  const a = (raw && typeof raw === "object" ? raw : {}) as Partial<BranchAsk>;
  const kind = KINDS.includes(a.kind as BranchKind) ? (a.kind as BranchKind) : "tag";
  const cmp = COMPARES.includes(a.cmp as BranchCompare) ? (a.cmp as BranchCompare) : "more";
  return { kind, tag: normalizeTag(typeof a.tag === "string" ? a.tag : ""), cmp, a: num(a.a, 50), b: num(a.b, 75) };
}

/**
 * What a branch asked before asks were tags and numbers: the old `cond`
 * words, read as the closest tag or number so nothing stored goes blank.
 */
const tagAsk = (name: string, kind: BranchKind = "tag"): BranchAsk => ({ kind, tag: name, cmp: "more", a: 50, b: 75 });
function askFromLegacy(cond: string | undefined, pct: number | undefined, tagName: string | undefined): BranchAsk {
  const tag = (name: string, kind?: BranchKind) => tagAsk(name, kind);
  switch (cond) {
    case "has_tag":
      return tag(normalizeTag(tagName ?? ""));
    case "not_has_tag":
      return tag(normalizeTag(tagName ?? ""), "notag");
    case "watched_pct":
      return { kind: "pct", tag: "", cmp: "more", a: num(pct, 50), b: 75 };
    case "attended":
      return tag("watched");
    case "watched_end":
      return tag("completed");
    case "clicked":
      return tag("clicked-offer");
    case "signed_up":
      return tag("customer");
    default:
      return tag("watched");
  }
}

/** The ask a stored branch carries, in any of the shapes it was ever written in. */
/** The shape a branch's condition took before `config.ask`; read only, never written. */
type LegacyCond =
  | { type: "fact"; fact: { op: string; name: string } }
  | { type: "signal"; cond: string; pct?: number }
  | undefined;
function readAsk(node: Pick<GraphNode, "config"> & { cond?: LegacyCond }): BranchAsk {
  const cfg = (node.config ?? {}) as Partial<BranchStepConfig> & { cond?: string; pct?: number; tagName?: string };
  if (cfg.ask) return cleanAsk(cfg.ask);
  const c = node.cond;
  if (c?.type === "fact") return askFromLegacy(c.fact.op === "has" ? "has_tag" : "not_has_tag", undefined, c.fact.name);
  if (c?.type === "signal") return askFromLegacy(c.cond, c.pct, undefined);
  return askFromLegacy(cfg.cond, cfg.pct, cfg.tagName);
}

/** One saved config (a graph node, or an old linear step) as the editor's step. */
export function configToStep(uid: string, type: string, config: unknown, cond?: LegacyCond): Step {
  const c = (config ?? {}) as Record<string, unknown>;
  switch (type) {
    case "room": {
      const r = c as unknown as RoomStepConfig;
      return {
        uid,
        type: "room",
        title: r.title ?? "",
        note: r.note ?? "",
        noteHtml: r.note_html ?? plainToRich(r.note ?? ""),
        attachments: Array.isArray(r.attachments) ? r.attachments : [],
        notifyEnabled: r.notify?.enabled !== false,
        notifySubject: r.notify?.subject ?? "",
      };
    }
    case "fact": {
      const f = c as unknown as FactActionConfig;
      return { uid, type: "fact", op: f.op === "remove" ? "remove" : "add", name: normalizeTag(f.name ?? "") };
    }
    case "webhook":
      return { uid, type: "webhook", url: (c.url as string) ?? "" };
    case "notify":
      return {
        uid,
        type: "notify",
        email: (c.email as string) ?? "",
        subject: (c.subject as string) ?? "",
        message: (c.message as string) ?? "",
      };
    case "wait":
      return {
        uid,
        type: "wait",
        mode: c.mode === "before" ? "before" : "after",
        num: num(c.num, 1),
        unit: (["minutes", "hours", "days"] as WaitUnit[]).includes(c.unit as WaitUnit) ? (c.unit as WaitUnit) : "days",
      };
    case "branch":
      return { uid, type: "branch", ask: readAsk({ config: config as StepConfig, cond }), yes: [], no: [] };
    default: {
      const e = c as unknown as EmailStepConfig;
      return {
        uid,
        type: "email",
        subject: friendlyTokens(e.subject ?? ""),
        preview: e.body ?? e.preview ?? "",
        // Seeded once, on read. A fallback at render time would put the old
        // words back the moment the host cleared the box.
        previewHtml: friendlyTokens(e.body_html ?? plainToRich(e.body ?? e.preview ?? "")),
        attachments: Array.isArray(e.attachments) ? e.attachments : [],
        button: typeof e.button === "string" ? e.button : "",
        images: Array.isArray(e.images) ? e.images : [],
        ...(e.metrics ? { metrics: e.metrics } : {}),
      };
    }
  }
}

/**
 * Every plain field is derived from the written html, so clearing the box really
 * clears the step. Leaving a stale `preview` or `body` behind would send the old
 * words, because the engine falls back to them.
 */
function emailConfig(s: EmailStep): EmailStepConfig {
  const html = (s.previewHtml ?? "").trim();
  const words = html ? richToText(s.previewHtml) : "";
  return {
    subject: s.subject,
    preview: words,
    body: words,
    ...(html ? { body_html: s.previewHtml } : {}),
    ...(s.attachments?.length ? { attachments: s.attachments } : {}),
    ...(s.button?.trim() ? { button: s.button.trim() } : {}),
    ...(s.images?.length ? { images: s.images } : {}),
    ...(s.metrics ? { metrics: s.metrics } : {}),
  };
}

/** The Room note, the same way: the html is the truth, the words follow it. */
function roomNote(noteHtml: string): { note?: string; note_html?: string } {
  const html = (noteHtml ?? "").trim();
  if (!html) return {};
  return { note: richToText(noteHtml), note_html: noteHtml };
}

/** The editor's step as the config the engine reads. */
export function stepConfig(s: Step): StepConfig {
  switch (s.type) {
    case "email":
      return emailConfig(s);
    case "room":
      return {
        title: s.title,
        ...(roomNote(s.noteHtml)),
        ...(s.attachments.length ? { attachments: s.attachments } : {}),
        notify: { enabled: s.notifyEnabled, ...(s.notifySubject.trim() ? { subject: s.notifySubject } : {}) },
      } satisfies RoomStepConfig;
    case "fact":
      return { op: s.op, kind: "tag", name: normalizeTag(s.name) } satisfies FactActionConfig;
    case "webhook":
      return { url: s.url.trim() } satisfies WebhookStepConfig;
    case "notify":
      return { email: s.email.trim(), subject: s.subject, message: s.message } satisfies NotifyStepConfig;
    case "wait":
      return { mode: s.mode, num: s.num, unit: s.unit } satisfies WaitStepConfig;
    case "branch": {
      const a = s.ask;
      const ask: BranchAsk =
        a.kind === "tag" || a.kind === "notag"
          ? { kind: a.kind, tag: normalizeTag(a.tag ?? "") }
          : { kind: a.kind, cmp: a.cmp ?? "more", a: a.a ?? 0, ...(a.cmp === "between" ? { b: a.b ?? 0 } : {}) };
      return { ask } satisfies BranchStepConfig;
    }
  }
}

// ── tree ⇄ graph ────────────────────────────────────────────────────────
/** A branch is the last step of its list; anything after it belongs on Yes. */
function withTail(list: Step[], tail: Step[]): Step[] {
  if (tail.length === 0) return list;
  const last = list[list.length - 1];
  if (last?.type === "branch") return [...list.slice(0, -1), { ...last, yes: withTail(last.yes, tail) }];
  return [...list, ...tail];
}
export function branchLast(list: Step[]): Step[] {
  const at = list.findIndex((s) => s.type === "branch");
  if (at < 0 || at === list.length - 1) return list;
  return withTail(list.slice(0, at + 1), list.slice(at + 1));
}

export function treeToGraph(steps: Step[]): AutomationGraph {
  const nodes: GraphNode[] = [];
  const used = new Set<string>();
  const id = (want: string): string => {
    let out = want;
    while (used.has(out)) out = newId();
    used.add(out);
    return out;
  };
  const emit = (list: Step[]): string | null => {
    const chain = branchLast(list);
    const ids = chain.map((s) => id(s.uid));
    chain.forEach((s, i) => {
      if (s.type === "branch") {
        const node: GraphNode = { id: ids[i], type: "branch", config: stepConfig(s) };
        nodes.push(node);
        node.whenTrue = emit(s.yes);
        node.whenFalse = emit(s.no);
        return;
      }
      nodes.push({ id: ids[i], type: s.type as ActionType, config: stepConfig(s), next: i < chain.length - 1 ? ids[i + 1] : null });
    });
    return ids[0] ?? null;
  };
  const start = emit(steps);
  return { start, nodes };
}

export function graphToTree(graph: AutomationGraph): Step[] {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const seen = new Set<string>();
  const walk = (from: string | null | undefined): Step[] => {
    const out: Step[] = [];
    let cursor = from ?? null;
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      const n = byId.get(cursor);
      if (!n) break;
      if (n.type === "branch") {
        out.push({ ...(configToStep(n.id, "branch", n.config, (n as { cond?: LegacyCond }).cond) as BranchStep), yes: walk(n.whenTrue), no: walk(n.whenFalse) });
        break;
      }
      out.push(configToStep(n.id, n.type, n.config));
      cursor = n.next ?? null;
    }
    return out;
  };
  return walk(graph.start);
}

// ── walking the tree ────────────────────────────────────────────────────
/** Where a list sits: the root, or one arm of a branch, however deep. */
export type ListPath = Array<{ uid: string; arm: "yes" | "no" }>;

function mapList(steps: Step[], path: ListPath, fn: (list: Step[]) => Step[]): Step[] {
  if (path.length === 0) return fn(steps);
  const [head, ...rest] = path;
  return steps.map((s) => (s.uid === head.uid && s.type === "branch" ? { ...s, [head.arm]: mapList(s[head.arm], rest, fn) } : s));
}

export function mapStep(steps: Step[], uid: string, fn: (step: Step) => Step): Step[] {
  return steps.map((s) => {
    if (s.uid === uid) return fn(s);
    if (s.type === "branch") return { ...s, yes: mapStep(s.yes, uid, fn), no: mapStep(s.no, uid, fn) };
    return s;
  });
}

/** Insert at `index`. A branch takes everything after it as its Yes path. */
export function insertStep(steps: Step[], path: ListPath, index: number, step: Step): Step[] {
  return mapList(steps, path, (list) => {
    if (step.type === "branch") return [...list.slice(0, index), { ...step, yes: list.slice(index) }];
    return [...list.slice(0, index), step, ...list.slice(index)];
  });
}

/** Where a step sits: the branches down to its list, and its place in that list. */
export function locateStep(steps: Step[], uid: string, path: ListPath = []): { path: ListPath; index: number; step: Step } | null {
  for (const [index, step] of steps.entries()) {
    if (step.uid === uid) return { path, index, step };
    if (step.type !== "branch") continue;
    const hit =
      locateStep(step.yes, uid, [...path, { uid: step.uid, arm: "yes" }]) ??
      locateStep(step.no, uid, [...path, { uid: step.uid, arm: "no" }]);
    if (hit) return hit;
  }
  return null;
}

export function removeStep(steps: Step[], path: ListPath, uid: string): Step[] {
  return mapList(steps, path, (list) => list.filter((s) => s.uid !== uid));
}

/**
 * Take a step out as the builder's Delete does. A branch leaves its Yes steps on the line in its place and lets
 * its No path go, because paths never join and the Yes path is the one that carried on from it.
 */
export function deleteStep(steps: Step[], path: ListPath, uid: string): Step[] {
  return mapList(steps, path, (list) => list.flatMap((s) => (s.uid !== uid ? [s] : s.type === "branch" ? s.yes : [])));
}

/** Undo a `deleteStep`: the step goes back at `index`, and a branch takes its Yes steps back off the line. */
export function undeleteStep(steps: Step[], path: ListPath, index: number, step: Step): Step[] {
  return mapList(steps, path, (list) => {
    const at = Math.min(index, list.length);
    const covered = step.type === "branch" ? step.yes.length : 0;
    return [...list.slice(0, at), step, ...list.slice(at + covered)];
  });
}

/**
 * Move a step to another place, before `beforeUid` in the list at `to` (or at its end). A branch never moves,
 * and nothing can go after a branch, since a branch ends its line.
 */
export function moveStep(steps: Step[], uid: string, to: ListPath, beforeUid: string | null): Step[] {
  const from = locateStep(steps, uid);
  if (!from || from.step.type === "branch" || beforeUid === uid) return steps;
  const without = removeStep(steps, from.path, uid);
  return mapList(without, to, (list) => {
    const fork = list.findIndex((s) => s.type === "branch");
    let at = beforeUid ? list.findIndex((s) => s.uid === beforeUid) : list.length;
    if (at < 0) at = list.length;
    if (fork >= 0) at = Math.min(at, fork);
    return [...list.slice(0, at), from.step, ...list.slice(at)];
  });
}

/**
 * The first part of a flow that still needs something before it can be closed or saved, with what to say: the
 * When stop with no tag, or an email with no subject or no message. Null when every part is ready.
 */
export function unfinished(trigger: TriggerJson, steps: Step[], only?: string): { uid: string; words: string } | null {
  if ((!only || only === "trigger") && !normalizeTag(trigger.value ?? "")) return { uid: "trigger", words: "Pick the tag that starts it." };
  for (const s of flatten(steps)) {
    if (s.type !== "email" || (only && s.uid !== only)) continue;
    // Why the html alone: it is what the save writes; the plain words are derived from it (`emailConfig`).
    const need = emailNeeds({ subject: s.subject, body_html: s.previewHtml });
    if (need) return { uid: s.uid, words: need === "subject" ? "Give the email a subject." : "Write the email’s message." };
  }
  return null;
}

export function flatten(steps: Step[]): Step[] {
  return steps.flatMap((s) => (s.type === "branch" ? [s, ...flatten(s.yes), ...flatten(s.no)] : [s]));
}

/** Every tag this flow names: the trigger, the stop rule, tag steps and branches. */
export function tagsInFlow(trigger: TriggerJson | null, stopWhen: StopWhen, steps: Step[]): string[] {
  const out = new Set<string>();
  const add = (t: string | undefined) => {
    const n = normalizeTag(t ?? "");
    if (n) out.add(n);
  };
  add(trigger?.value);
  add(stopWhen?.tag);
  for (const t of stopWhen?.more ?? []) add(t);
  for (const s of flatten(steps)) {
    if (s.type === "fact") add(s.name);
    if (s.type === "branch" && (s.ask.kind === "tag" || s.ask.kind === "notag")) add(s.ask.tag);
  }
  return [...out];
}
